import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Users, MessageCircle, Settings, Building2, ChevronLeft, ChevronRight, UserCheck, Tag, FileText, Shield } from "lucide-react";

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: Home },
  { name: "Administrators", href: "/administrators", icon: Users },
  { name: "Users of ARiA", href: "/organizations", icon: Building2 },
  { name: "Risk Profiles", href: "/customer-categories", icon: Tag },
  { name: "AP Archetypes", href: "/ap-archetypes", icon: FileText },
  { name: "Governance Overlays", href: "/governance-overlays", icon: Shield },
  { name: "Conversations", href: "/whatsapp-manager", icon: MessageCircle },
  { name: "Settings", href: "/settings", icon: Settings },
];

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const [backendVersion, setBackendVersion] = useState<string>("");
  const frontendVersion = process.env.REACT_APP_FRONTEND_VERSION || "unknown";

  useEffect(() => {
    if (!isCollapsed) {
      fetch((process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000") + "/health")
        .then((res) => res.json())
        .then((data) => {
          if (data && data.data && data.data.version) {
            setBackendVersion(data.data.version);
          } else {
            setBackendVersion("unknown");
          }
        })
        .catch(() => setBackendVersion("unknown"));
    }
  }, [isCollapsed]);

  return (
    <aside
      className={`fixed left-0 top-0 z-40 h-screen bg-white border-r border-slate-200 transition-all duration-300 ${isCollapsed ? "w-16" : "w-64"}`}
    >
      {/* Logo section */}
      <div className={`flex items-center h-16 border-b border-slate-200 transition-all duration-300 ${isCollapsed ? "justify-center px-2" : "justify-center px-4"}`}>
        <img
          src="/logo192.png"
          alt="ARiA Logo"
          className={isCollapsed ? "w-8 h-8 object-contain" : "w-12 h-12 object-contain"}
        />
      </div>
      {/* Navigation */}
      <nav className={`mt-8 ${isCollapsed ? "px-2" : "px-4"}`} style={{ height: "calc(100vh - 160px)" }}>
        <ul className="space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            if (isCollapsed) {
              return (
                <li key={item.name}>
                  <div className="flex items-center justify-center w-12 h-12 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer">
                    <Link
                      to={item.href}
                      className={`flex items-center justify-center w-12 h-12 ${isActive ? "bg-blue-100 text-blue-600" : ""} rounded-lg transition-colors`}
                    >
                      <item.icon className="w-5 h-5" />
                    </Link>
                  </div>
                </li>
              );
            }
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors ${isActive ? "bg-blue-100 text-blue-600" : ""}`}
                  style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      {/* Collapse Button */}
      <div className={`absolute bottom-4 left-0 right-0 flex flex-col items-end ${isCollapsed ? "justify-center px-2" : "px-4"}`}>
        <button
          onClick={onToggleCollapse}
          className={`hover:bg-slate-100 border border-slate-200 transition-all duration-300 ${isCollapsed ? "h-8 w-8 p-0 flex items-center justify-center rounded-lg" : "h-10 w-full justify-center px-4 py-2 flex items-center gap-2 rounded-lg"}`}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
        {!isCollapsed && (
          <div className="mt-2 text-xs text-slate-400 w-full text-right pr-1">
            (FN: v{frontendVersion || "unknown"}, BN: v{backendVersion || "unknown"})
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar; 